import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Loader2 } from 'lucide-react';

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { changePassword, user } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);
    try {
      await changePassword(oldPassword, newPassword);
      toast.success('Mot de passe modifié avec succès');
      navigate('/');
    } catch (error) {
      console.error('Change password error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="glass rounded-2xl p-8 fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-600/20 mb-4">
              <KeyRound className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Changer le mot de passe
            </h1>
            {user?.must_change_password && (
              <p className="text-yellow-400 text-sm">⚠️ Vous devez changer votre mot de passe temporaire</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="oldPassword" className="text-gray-300">Mot de passe actuel</Label>
              <Input
                data-testid="old-password-input"
                id="oldPassword"
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="bg-gray-900/50 border-gray-700 text-white"
                placeholder="Entrez votre mot de passe actuel"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="newPassword" className="text-gray-300">Nouveau mot de passe</Label>
              <Input
                data-testid="new-password-input"
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-gray-900/50 border-gray-700 text-white"
                placeholder="Minimum 8 caractères"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-gray-300">Confirmer le mot de passe</Label>
              <Input
                data-testid="confirm-password-input"
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-gray-900/50 border-gray-700 text-white"
                placeholder="Répétez le nouveau mot de passe"
                disabled={loading}
              />
            </div>

            <Button
              data-testid="change-password-btn"
              type="submit"
              className="w-full bg-yellow-600 hover:bg-yellow-700 h-12 text-lg"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <KeyRound className="w-5 h-5 mr-2" />
                  Changer le mot de passe
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
